import React, { useEffect } from 'react';
import dayjs from 'dayjs';
import { IPaymentAggregate } from '..';
import arrow from '../../../images/down_arrow.svg';
import { PaymentDetail } from '../../../data-models';
import { HeaderTextSmall } from '../../base/tw-components';

const dateFormat = 'MM/DD/YYYY';

const StyledLine = () => {
  return (
    <div className="tw-bg-blue-100 tw-border-none tw-text-blue-100 tw-h-px tw-my-2 tw-mx-auto tw-w-11/12"></div>
  );
};

const Arrow = ({ rotate }: { rotate: string }) => {
  return (
    <img
      alt="Expand"
      src={arrow}
      className={`tw-cursor-pointer tw-mr-2 tw-w-11 tw-transform tw-transition-all tw-duration-500 tw-user-select-none ${
        rotate === 'true' ? 'tw-rotate-180' : 'tw-rotate-0'
      } md:tw-mr-4`}
    />
  );
};
interface Props {
  children: React.ReactNode;
}

interface DetailHeaderProps {
  children: React.ReactNode;
  fontSize?: string;
}

const Container = ({ children }: Props) => {
  return (
    <div className="tw-px-4 md:tw-px-8 tw-pt-4 tw-pb-4 tw-text-unm-adhoc2">
      {children}
    </div>
  );
};

const DetailHeader = ({ children, fontSize = 'sm' }: DetailHeaderProps) => {
  return (
    <span
      className={`tw-font-semibold tw-font-sans tw-text-${fontSize} tw-text-unm-adhoc2`}
    >
      {children}
    </span>
  );
};

interface ContainerProps {
  expanded: boolean;
  children: React.ReactNode;
}

const SummaryContainer = ({ expanded, children }: ContainerProps) => {
  return (
    <div
      className={`tw-flex tw-flex-col md:tw-flex-row tw-w-full tw-justify-between tw-items-center tw-px-4 tw-pt-4 tw-pb-2 md:tw-px-6 md:tw-py-8 tw-rounded-lg ${
        expanded
          ? 'tw-border-b tw-border-solid tw-border-unm-border1 tw-border-0'
          : ''
      }`}
    >
      {children}
    </div>
  );
};

const DetailsContainer = ({ children }: Props) => {
  return (
    <div className="tw-unm-bg-[#fcfcfc] tw-w-full tw-px-4 tw-py-4 md:tw-px-6 md:tw-py-8">
      {children}
    </div>
  );
};

interface StatusIconProps {
  future: boolean | undefined;
  children: React.ReactNode;
}

const StatusIcon = ({ children, future }: StatusIconProps) => {
  return (
    <div
      className={`tw-opacity-100 tw-rounded-lg tw-text-center tw-py-2 tw-mb-2 md:tw-mb-0 tw-w-36 tw-mr-4 ${
        future
          ? 'tw-bg-unm-adhoc1 tw-text-unm-interactive01-400'
          : 'tw-bg-[#D9F2EA] tw-text-[#04865D]'
      } tw-font-ubuntu tw-font-regular tw-text-sm`}
    >
      {children}
    </div>
  );
};

interface PaymentProps {
  id?: string;
  keyItem?: string;
  onClick: () => void;
  children: React.ReactNode;
}

export const PaymentItem = ({
  id,
  keyItem,
  onClick,
  children,
}: PaymentProps) => {
  return (
    <button
      id={id}
      key={keyItem}
      onClick={onClick}
      className="tw-bg-white tw-border tw-border-solid tw-border-unm-border1 tw-rounded-lg tw-justify-between tw-relative tw-z-2 tw-w-full tw-p-0 tw-m-0"
    >
      {children}
    </button>
  );
};

interface TextProps {
  id?: string;
  color?: string;
  align?: string;
  className?: string;
  children: React.ReactNode;
}

const Text = ({
  id,
  color = 'unm-interactive01-800',
  align,
  className,
  children,
}: TextProps) => {
  return (
    <div
      id={id}
      className={`tw-font-ubuntu tw-font-bold tw-text-sm tw-text-${align} tw-text-${color} ${className}`}
    >
      {children}
    </div>
  );
};

interface DetailAmountProps {
  id?: string;
  color?: string;
  align?: string;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

const DetailAmount = ({
  id,
  color = 'unm-secondary01-600',
  align = 'right',
  className,
  style,
  children,
}: DetailAmountProps) => {
  return (
    <span
      id={id}
      className={`tw-font-ubuntu tw-font-normal tw-text-sm tw-text-${color} tw-text-${align} ${className}`}
      style={style}
    >
      {children}
    </span>
  );
};

interface InfoBoxProps {
  className?: string;
  children: React.ReactNode;
}

const InfoBox = ({ className, children }: InfoBoxProps) => {
  return (
    <div
      className={`tw-flex tw-items-center tw-rounded-lg tw-bg-[#eff3f8] tw-p-2 tw-font-sans tw-text-sm tw-text-unm-adhoc2 tw-text-left ${className}`}
    >
      {children}
    </div>
  );
};

export const presentablePaymentMethod = (paymentMethod: string) => {
  if (!paymentMethod) {
    return paymentMethod;
  }

  const pmMap: { [name: string]: string } = {
    check: 'Check',
    eft: 'EFT',
    zelle: 'Zelle',
  };

  let result = pmMap[paymentMethod];

  if (!result) {
    result = paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1);
  }

  return result;
};

export const presentableDetailReason = (detailReason: string | undefined) => {
  if (!detailReason) {
    return detailReason;
  }

  const drMap: { [name: string]: string } = {
    'Auto Gross Entitlement': 'Gross Pay',
  };

  let result = drMap[detailReason];

  if (!result) {
    result = detailReason;
  }

  return result.toUpperCase();
};

export const getPaymentText = (isCheckCutting: boolean, future: boolean) => {
  if (future && !isCheckCutting) {
    return 'EMPLOYER TO BE NOTIFIED';
  } else if (!future && !isCheckCutting) {
    return 'EMPLOYER NOTIFIED';
  } else if (!future && isCheckCutting) {
    return 'ISSUED';
  } else {
    return 'TO BE ISSUED';
  }
};

export const getPaymentAmount = (
  isCheckCutting: boolean,
  futurePayment: boolean,
  item: any,
) => {
  let paymentText: string;

  if (!isCheckCutting) {
    paymentText = 'Payment to be made by Employer';
  } else if (futurePayment) {
    paymentText = `Estimated Payment of $${item.paymentAmount}`;
  } else {
    paymentText = `Payment of $${item.paymentAmount}`;
  }

  return (
    <Text className="tw-text-xl tw-text-unm-interactive01-800 tw-w-full amount">
      {paymentText}
    </Text>
  );
};

const getDetailsContainer = (
  isCheckCutting: boolean,
  futurePayment: boolean,
  item: any,
  index: number,
  grossPay: any,
) => {
  return (
    <DetailsContainer>
      <div id={`payment-details-${index}`} className="tw-flex tw-flex-col">
        {futurePayment && isCheckCutting && (
          <div id={`payment-details-${index}`} className="tw-flex tw-flex-col">
            <InfoBox className="tw-mb-3 tw-w-full">
              <span
                className="k-icon k-i-info tw-mr-2"
                style={{ color: '#015294' }}
              ></span>
              <span>
                This payment is an estimate and subject to change based on plan
                provisions. Please check back on the release date for payment
                amount.
              </span>
            </InfoBox>
          </div>
        )}
        <Text
          className="tw-w-full tw-py-2 "
          color={'unm-interactive01-700'}
          align="left"
        >
          PAYMENT DETAILS
        </Text>
        <div className="tw-flex tw-flex-col md:tw-flex-row tw-mt-1 tw-text-unm-secondary01-600">
          <Text className="tw-flex tw-flex-row md:tw-flex-col tw-w-full md:tw-w-4/12 tw-text-left tw-py-2">
            <DetailHeader>PERIOD: </DetailHeader>
            <DetailAmount align="left" color="unm-adhoc2">
              {dayjs(item.periodStartDate).format(dateFormat)} -{' '}
              {dayjs(item.periodEndDate).format(dateFormat)}
            </DetailAmount>
          </Text>
          <div className="tw-flex tw-w-full md:tw-w-8/12 tw-text-left tw-py-2">
            <Text className="tw-w-1/2">
              <DetailHeader>PAID BY: </DetailHeader>
              <DetailAmount color="unm-adhoc2">
                {isCheckCutting ? 'Unum' : 'Your employer'}
              </DetailAmount>
            </Text>
            <Text className="tw-w-1/2 tw-text-right">
              <DetailHeader>METHOD: </DetailHeader>
              <DetailAmount color="unm-adhoc2">
                {isCheckCutting && !futurePayment && item.paymentMethod
                  ? presentablePaymentMethod(item.paymentMethod)
                  : 'N/A'}
              </DetailAmount>
            </Text>
          </div>
        </div>
        {isCheckCutting && (
          <>
            <StyledLine />
            <Text
              className="tw-w-full tw-py-2"
              color={'unm-interactive01-700'}
              align="left"
            >
              PAYMENT BREAKDOWN
            </Text>
            <div
              id={`payment-item-${index}-detail-grosspay`}
              className="tw-flex tw-flex-row tw-py-2"
            >
              <HeaderTextSmall
                id={`payment-item-${index}-detail-grosspay-reason`}
                fontSize="sm"
                className="tw-w-9/12 tw-pr-4"
              >
                {presentableDetailReason(grossPay.detailReason)}
              </HeaderTextSmall>
              <DetailAmount
                id={`payment-item-${index}-detail-grosspay-amount`}
                className="tw-w-3/12"
                style={{ marginLeft: 'auto' }}
              >
                ${grossPay.detailAmount}
              </DetailAmount>
            </div>
            <StyledLine />
            <Text
              className="tw-w-full tw-py-2"
              color="unm-interactive01-700"
              align="left"
            >
              <strong>ADJUSTMENTS</strong>
            </Text>
            {item.paymentDetails?.map(
              (detail: any, i: number) =>
                presentableDetailReason(detail.detailReason) !==
                  'GROSS PAY' && (
                  <div
                    id={`payment-item-${index}-detail-${i}`}
                    key={`payments-item-detail${detail.detailReason}-${i}`}
                    className="tw-flex tw-py-2"
                  >
                    <HeaderTextSmall
                      id={`payment-item-${index}-detail-${i}-reason`}
                      className="tw-w-9/12 tw-pr-4"
                      fontSize="sm"
                    >
                      {presentableDetailReason(detail.detailReason)}
                    </HeaderTextSmall>
                    <DetailAmount
                      id={`payment-item-${index}-detail-${i}-amount`}
                      className="tw-w-3/12"
                      color="unm-adhoc2"
                    >
                      ${detail.detailAmount}
                    </DetailAmount>
                  </div>
                ),
            )}
            <StyledLine />
            <div className="tw-flex tw-py-2">
              <HeaderTextSmall fontSize="sm" className="tw-w-9/12 tw-pr-4">
                NET TOTAL
              </HeaderTextSmall>
              <DetailAmount
                style={{ marginLeft: 'auto' }}
                className="tw-w-3/12"
              >
                ${item.paymentAmount}
              </DetailAmount>
            </div>
          </>
        )}
        {!isCheckCutting && (
          <div className="tw-flex tw-flex-col tw-mt-3">
            <InfoBox>
              <span
                className="k-icon k-i-info tw-mr-2"
                style={{ color: '#015294' }}
              ></span>
              <i>
                Your employer will issue your payment(s). Please direct all
                questions regarding your benefit payments to your employer.
              </i>
            </InfoBox>
          </div>
        )}
      </div>
    </DetailsContainer>
  );
};

/**
 * MyItemRender - Renders custom items in the ListView component
 * @param props
 */
const ItemRender: (props: {
  dataItem: IPaymentAggregate;
  [name: string]: any;
}) => React.ReactElement = (props) => {
  const item = props.dataItem;
  const index = props.index;
  const [showDetails, setShowDetails] = React.useState(false);
  const [grossPay, setGrossPay] = React.useState<PaymentDetail>({
    detailReason: '',
    detailAmount: '',
    paymentDetailId: '',
  });
  const isCheckCutting = item.checkCutting === 'Check Cutting';
  const futurePayment = item.isFuturePayment || false;
  const detailArray = item.paymentDetails;

  useEffect(() => {
    detailArray?.forEach((d) => {
      if (
        d.detailReason &&
        presentableDetailReason(d.detailReason) === 'GROSS PAY'
      ) {
        setGrossPay(d);
      }
    });
  }, [detailArray]);

  return (
    <Container>
      <PaymentItem
        id={`payments-list-item-${index}`}
        key={`payments-list-item-${index}`}
        onClick={() => setShowDetails(!showDetails)}
      >
        <SummaryContainer expanded={showDetails}>
          <div className="tw-text-center tw-mb-4 md:tw-text-left md:tw-mb-0">
            <Text color={'unm-interactive01-700'}>
              {getPaymentText(isCheckCutting, futurePayment)}{' '}
              {dayjs(item.paymentDate).format(dateFormat)}
            </Text>
            {getPaymentAmount(isCheckCutting, futurePayment, item)}
            <Text
              className="tw-text-unm-secondary01-700 tw-font-normal tw-font-sans tw-text-sm"
              id="paymentTtem"
            >
              {item.claimName} ({item.caseId})
            </Text>
          </div>
          <div className="tw-flex tw-items-center tw-flex-col md:tw-flex-row">
            <StatusIcon future={futurePayment}>
              {futurePayment ? 'UPCOMING' : 'PAID'}
            </StatusIcon>
            <Arrow rotate={`${showDetails}`} />
          </div>
        </SummaryContainer>

        {showDetails &&
          getDetailsContainer(
            isCheckCutting,
            futurePayment,
            item,
            index,
            grossPay,
          )}
      </PaymentItem>
    </Container>
  );
};

export default ItemRender;
